/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
export function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

/**
 * Adds a fast click event listener to an element.
 * @param {HTMLElement} el - The element to add the event listener to.
 * @param {Function} handler - The function to call when the event is triggered.
 * @returns {Function} - A function to remove the event listener.
 */
export function addFastClick(el, handler) {
    let touchFired = false;

    const onTouchEnd = (e) => {
        touchFired = true;
        handler(e);
        // Reset after one frame to avoid duplicate mouseup
        requestAnimationFrame(() => {
            touchFired = false;
        });
    };

    const onMouseUp = (e) => {
        if (touchFired) return;
        handler(e);
    };

    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('mouseup', onMouseUp);

    return () => {
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('mouseup', onMouseUp);
    };
}

/**
 * Transforms a string from kebab-case to PascalCase.
 * @param {string} str - The input string in kebab-case.
 * @returns {string} - The transformed string in PascalCase.
 */
export function kebabToPascalCase(str) {
    return str
        .split('-') // Split the string by hyphens
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(''); // Join the words back together
}

/**
 * Logs a message to the DOM by creating an <h4> element with the message,
 * appending it to the body, and removing it after a specified delay.
 *
 * @param {string} message - The message to display in the <h4> element.
 * @param {number} [delay=3000] - The delay in milliseconds before removing the <h4> element. Default is 3000ms.
 */
export function logDOM(message, delay = 8000) {
    if (window.Shopify.theme.role != 'development') return;
    const h4 = document.createElement('h4');
    h4.textContent = message;
    h4.style.color = 'red';

    const logDomContainer = document.querySelector('[data-dom-log]') || document.body;

    logDomContainer.prepend(h4);

    setTimeout(() => {
        logDomContainer.removeChild(h4);
    }, delay);
}

/**
 * Populates an HTML template with the provided variables. Replaced variables wrapped in {# variable_name #}
 *
 * @param {HTMLTemplateElement} template - The HTML template element to populate.
 * @param {Object} variables - An object containing the variables to populate the template with.
 * @returns {DocumentFragment} - A document fragment containing the populated template content.
 * @throws {Error} - Throws an error if the template is not a valid HTMLTemplateElement or if the variables object is invalid.
 */
export function populateTemplate(template, variables) {
    if (!template || !(template instanceof HTMLTemplateElement)) {
        throw new Error('Invalid template element');
    }
    if (typeof variables !== 'object' || variables === null) {
        throw new Error('Invalid variables object');
    }

    const tempDiv = document.createElement('div');
    tempDiv.appendChild(template.content.cloneNode(true));

    tempDiv.innerHTML = tempDiv.innerHTML.replace(/{#\s*(.*?)\s*#}/g, (match, variableName) => {
        const value = variables[variableName];
        return value !== undefined && value !== null ? value : '';
    });

    const clone = document.createDocumentFragment();
    while (tempDiv.firstChild) {
        clone.appendChild(tempDiv.firstChild);
    }

    return clone;
}

/**
 * Waits for the animation to end on the specified element.
 *
 * @param {HTMLElement} element - The element to wait for the animation to end on.
 * @param {Function} [callback] - Optional callback function to execute when the animation ends.
 * @returns {Promise<void>} A promise that resolves when the animation ends.
 */
export async function afterAnimationEnd(element, callback) {
    return new Promise((resolve) => {
        function onEnd(event) {
            if (event.target !== element) return;

            element.removeEventListener('animationend', onEnd);
            element.removeEventListener('transitionend', onEnd);

            if (typeof callback === 'function') {
                callback();
            } else {
                console.warn('callback was not valid function', callback);
            }

            resolve();
        }

        element?.addEventListener('animationend', onEnd);
        element?.addEventListener('transitionend', onEnd);
    });
}

/**
 * Executes the provided callback function after the current call stack has cleared.
 *
 * @param {Function} callback - The function to be executed after the call stack clears.
 */
export function afterCallstack(callback) {
    setTimeout(() => {
        requestAnimationFrame(callback);
    }, 0);
}
